import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';

interface ReviewPosition {
    id: string;
    symbol: string;
    market: string;
    status: string;
    closedAt: string;
    realizedPnl: number;
    realizedPnlPct: number;
    review: {
        reviewText: string;
        stopLossRespected: boolean;
        learningTags: string | null;
    };
}

export default function Reviews() {
    const [reviews, setReviews] = useState<ReviewPosition[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadReviews();
    }, []);

    async function loadReviews() {
        setIsLoading(true);
        const response = await api.getReviews();
        if (response.success) {
            setReviews(response.data || []);
        }
        setIsLoading(false);
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'short',
        });
    };

    const getPnlClass = (pnl: number | null | undefined) => {
        if (pnl == null) return '';
        return pnl >= 0 ? 'text-success' : 'text-danger';
    };

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">복기 노트</h1>
                    <p className="text-muted">완료된 매매의 교훈을 모아봅니다</p>
                </div>
            </div>

            {isLoading ? (
                <div className="loading-screen">
                    <div className="loading-spinner"></div>
                </div>
            ) : reviews.length === 0 ? (
                <div className="card">
                    <div className="empty-state">
                        <p>작성된 복기가 없습니다</p>
                        <Link to="/positions?status=CLOSED" className="btn btn-primary mt-md">
                            청산된 포트폴리오에서 복기 작성하기
                        </Link>
                    </div>
                </div>
            ) : (
                <div className="grid gap-lg">
                    {reviews.map((item) => (
                        <div key={item.id} className="card">
                            <div className="flex justify-between items-start mb-md">
                                <div>
                                    <div className="flex items-center gap-sm mb-xs">
                                        <Link to={`/positions/${item.id}`} className="font-bold text-lg hover:underline">
                                            {item.symbol}
                                        </Link>
                                        <span className="text-muted text-sm">{item.market}</span>
                                        <span className={`badge ${item.review.stopLossRespected ? 'badge-success' : 'badge-danger'}`}>
                                            손절 {item.review.stopLossRespected ? '준수' : '미준수'}
                                        </span>
                                    </div>
                                    <p className="text-sm text-muted">
                                        청산일: {item.closedAt ? formatDate(item.closedAt) : '-'}
                                    </p>
                                </div>
                                <div className={`text-lg font-bold ${getPnlClass(item.realizedPnlPct)}`}>
                                    {item.realizedPnlPct > 0 ? '+' : ''}{item.realizedPnlPct.toFixed(2)}%
                                </div>
                            </div>

                            <div className="review-content bg-secondary rounded p-md mb-md">
                                <p style={{ whiteSpace: 'pre-wrap' }}>{item.review.reviewText}</p>
                            </div>

                            {item.review.learningTags && (
                                <div className="flex flex-wrap gap-xs">
                                    {JSON.parse(item.review.learningTags).map((tag: string, idx: number) => (
                                        <span key={idx} className="badge badge-neutral bg-surface">#{tag}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
